import backgroundReplacerPreview from "@/assets/preview-background-replacer.webp";
import captionVideosPreview from "@/assets/preview-caption-videos.webp";
const CGI_VIDEOS_CDN = "https://cdn.pixro.ai/dab06e54-2c2b-4365-b327-29b962b365eb.mp4";
import editWithAiPreview from "@/assets/preview-edit-with-ai.webp";
const FASHION_REELS_CDN = "https://cdn.pixro.ai/a9899ba8-1027-4449-ab7b-82cd06847194.mp4";
import flatLayPreview from "@/assets/preview-flat-lay.webp";
import ghostMannequinPreview from "@/assets/preview-ghost-mannequin.webp";
import removeBackgroundPreview from "@/assets/preview-remove-background.webp";
import resizePreview from "@/assets/preview-resize.webp";
const TALKING_AVATARS_CDN = "https://cdn.pixro.ai/93523cad-2bdb-447e-8dd7-6150c44a0af4.mp4";
const UGC_VIDEOS_CDN = "https://cdn.pixro.ai/4b505bf2-046e-44f7-9701-474701404285.mp4";
const PRODUCT_VIDEOS_CDN = "https://cdn.pixro.ai/fc50fa4b-b4bd-411b-b2b0-f95e70e37d76.mp4";
import upscaleVideoPreview from "@/assets/preview-upscale-video.webp";
import upscalePreview from "@/assets/preview-upscale.webp";
import virtualTryonPreview from "@/assets/preview-virtual-tryon.webp";
import LazyVideo from "@/components/LazyVideo";
import { toolAppHref, toolLandingHref } from "@/lib/tools";
import {
    CircleUserRound,
    Clapperboard,
    Crop,
    Eraser,
    Film,
    Image as ImageIcon,
    Maximize2,
    MessageSquareText,
    Paintbrush,
    Pencil,
    Shirt,
    Sparkles,
    Store,
    Video,
    ZoomIn,
    type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

type PreviewType = "video" | "image";

type Tool = {
    label: string;
    icon: LucideIcon;
    highlight?: boolean;
    preview?: string;
    previewType?: PreviewType;
    comingSoon?: boolean;
    /** Send clicks to the tool's SEO landing page instead of straight into the app. */
    useLandingPage?: boolean;
};

const videos: Tool[] = [
    {
        label: "Product videos",
        icon: Clapperboard,
        preview: PRODUCT_VIDEOS_CDN,
        previewType: "video",
        useLandingPage: true,
    },
    {
        label: "UGC videos",
        icon: Video,
        preview: UGC_VIDEOS_CDN,
        previewType: "video",
        useLandingPage: true,
    },
    {
        label: "AI Fashion reels",
        icon: Film,
        preview: FASHION_REELS_CDN,
        previewType: "video",
        useLandingPage: true,
    },
    {
        label: "CGI videos",
        icon: Sparkles,
        preview: CGI_VIDEOS_CDN,
        previewType: "video",
        comingSoon: true,
    },
    {
        label: "Talking avatars",
        icon: CircleUserRound,
        preview: TALKING_AVATARS_CDN,
        previewType: "video",
        comingSoon: true,
    },
    {
        label: "Upscale video",
        icon: Maximize2,
        preview: upscaleVideoPreview,
        previewType: "image",
        comingSoon: true,
    },
    {
        label: "Caption videos",
        icon: MessageSquareText,
        preview: captionVideosPreview,
        previewType: "image",
        comingSoon: true,
    },
];

const photoshoot: Tool[] = [
    {
        label: "Background replacer",
        icon: ImageIcon,
        preview: backgroundReplacerPreview,
        previewType: "image",
        useLandingPage: true,
    },
    // {
    //     label: "Product showcase",
    //     icon: Storefront,
    //     preview: productShowcasePreview,
    //     previewType: "image",
    // },
    {
        label: "Virtual Try-On",
        icon: Shirt,
        preview: virtualTryonPreview,
        previewType: "image",
        useLandingPage: true,
    },
    {
        label: "Ghost mannequin",
        icon: Paintbrush,
        preview: ghostMannequinPreview,
        previewType: "image",
        useLandingPage: true,
    },
    {
        label: "Flat-lay",
        icon: Store,
        preview: flatLayPreview,
        previewType: "image",
        useLandingPage: true,
    },
];

const edit: Tool[] = [
    {
        label: "Remove background",
        icon: Eraser,
        preview: removeBackgroundPreview,
        previewType: "image",
        useLandingPage: true,
    },
    {
        label: "Upscale",
        icon: ZoomIn,
        preview: upscalePreview,
        previewType: "image",
        useLandingPage: true,
    },
    {
        label: "Resize",
        icon: Crop,
        preview: resizePreview,
        previewType: "image",
        useLandingPage: true,
    },
    {
        label: "Edit with AI",
        icon: Pencil,
        preview: editWithAiPreview,
        previewType: "image",
        useLandingPage: true,
    },
];

interface AiToolsMenuProps {
    open: boolean;
    onClose: () => void;
}

const Column = ({
    title,
    items,
    onHover,
}: {
    title: string;
    items: Tool[];
    onHover: (tool: Tool) => void;
}) => (
    <div className="flex flex-col my-0">
        <h4 className="text-xs font-semibold tracking-widest text-muted-foreground mb-[4px] mx-[8px]">
            {title}
        </h4>
        <ul className="flex flex-col gap-1">
            {items.map((tool) => {
                const Icon = tool.icon;
                const landing = tool.useLandingPage
                    ? toolLandingHref(tool.label)
                    : toolAppHref(tool.label);
                const isInternal = landing.startsWith("/");
                if (tool.comingSoon) {
                    return (
                        <li key={tool.label}>
                            {isInternal ? (
                                <Link
                                    to={landing}
                                    onMouseEnter={() => onHover(tool)}
                                    className="flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm text-foreground/40 hover:bg-muted/40 hover:text-foreground/60 transition-colors"
                                >
                                    <Icon className="h-4 w-4" />
                                    <span>{tool.label}</span>
                                    <span className="ml-auto text-[10px] font-medium tracking-wide bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5">
                                        Soon
                                    </span>
                                </Link>
                            ) : (
                                <span
                                    onMouseEnter={() => onHover(tool)}
                                    className="flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm text-foreground/40 cursor-default select-none"
                                >
                                    <Icon className="h-4 w-4" />
                                    <span>{tool.label}</span>
                                    <span className="ml-auto text-[10px] font-medium tracking-wide bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5">
                                        Soon
                                    </span>
                                </span>
                            )}
                        </li>
                    );
                }
                return (
                    <li key={tool.label}>
                        {isInternal ? (
                            <Link
                                to={landing}
                                onMouseEnter={() => onHover(tool)}
                                className={`flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted/40 ${
                                    tool.highlight
                                        ? "text-primary hover:text-primary"
                                        : "text-foreground/90 hover:text-foreground"
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{tool.label}</span>
                            </Link>
                        ) : (
                            <a
                                href={landing}
                                target="_blank"
                                rel="noopener noreferrer"
                                onMouseEnter={() => onHover(tool)}
                                className={`flex items-center gap-3 whitespace-nowrap rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted/40 ${
                                    tool.highlight
                                        ? "text-primary hover:text-primary"
                                        : "text-foreground/90 hover:text-foreground"
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{tool.label}</span>
                            </a>
                        )}
                    </li>
                );
            })}
        </ul>
    </div>
);

const AiToolsMenu = ({ open, onClose }: AiToolsMenuProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [hoveredTool, setHoveredTool] = useState<Tool | null>(null);
    useLocation(); // keep router context available

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            ref={ref}
            className="fixed left-1/2 top-10 z-50 w-[926px] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-white/10 bg-card/40 backdrop-blur-2xl p-8 shadow-2xl pb-[24px] pt-[24px] pl-[24px] pr-[24px]"
        >
            <div className="flex gap-8 items-stretch">
                <div className="flex-shrink-0">
                    <Column
                        title="VIDEOS"
                        items={videos}
                        onHover={setHoveredTool}
                    />
                </div>
                <div className="flex-shrink-0">
                    <Column
                        title="PHOTOSHOOT"
                        items={photoshoot}
                        onHover={setHoveredTool}
                    />
                </div>
                <div className="flex-shrink-0">
                    <Column
                        title="EDIT"
                        items={edit}
                        onHover={setHoveredTool}
                    />
                </div>
                <div className="flex-1 min-w-[260px] flex items-stretch justify-start">
                    <div className="aspect-square h-full overflow-hidden rounded-2xl">
                        {(() => {
                            const activeTool = hoveredTool ?? videos[0];
                            if (
                                activeTool.previewType === "video" &&
                                activeTool.preview
                            ) {
                                return (
                                    <LazyVideo
                                        key={activeTool.preview}
                                        src={activeTool.preview}
                                        poster={virtualTryonPreview}
                                        className="h-full w-full object-cover"
                                    />
                                );
                            }
                            return (
                                <img
                                    key={activeTool.preview ?? activeTool.label}
                                    src={
                                        activeTool.preview ??
                                        virtualTryonPreview
                                    }
                                    alt={activeTool.label}
                                    loading="lazy"
                                    width={768}
                                    height={768}
                                    className="h-full w-full object-cover"
                                />
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiToolsMenu;
